import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DropdownItems, DropdownListItem, Icon } from '@hm/fundamental'
import { mdiChevronDown } from '@mdi/js'
import { useTranslation } from '../../../hooks/useTranslation'

interface TimezoneSelectProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholderKey: string
  searchPlaceholderKey: string
  noResultsKey: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  /** Extra zone(s) to keep selectable even if omitted from Intl.supportedValuesOf, e.g. the originally stored value */
  extraOptions?: string[]
}

export const CountryConfigsTimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  disabled = false,
  placeholderKey,
  searchPlaceholderKey,
  noResultsKey,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  extraOptions,
}) => {
  const {t} = useTranslation()

  const timezoneOptions = useMemo(() => {
    const canonical = Intl.supportedValuesOf('timeZone')
    const extras = Array.from(new Set(['UTC', ...(extraOptions ?? [])])).filter((tz) => tz && !canonical.includes(tz))
    return [...canonical, ...extras].sort().map((tz) => ({
      value: tz,
      label: tz,
    }))
  }, [extraOptions])

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const wrapperRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) {
      return timezoneOptions
    }

    return timezoneOptions.filter((option) =>
      option.label.toLowerCase().includes(query),
    )
  }, [timezoneOptions, search])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setSearch('')
  }, [])

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        closeDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () =>
      document.removeEventListener('mousedown', handleClickOutside)
  }, [closeDropdown])

  return (
    <div ref={wrapperRef} className="relative w-[240px]">
     <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={Boolean(ariaDescribedBy)}
        className={`flex items-center h-10 px-3 rounded-md bg-container_background outline outline-1 outline-offset-[-1px] outline-border_highlight_10 w-full ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
        onClick={() => {
          if (disabled) return

          setSearch('')
          setIsOpen((prev) => !prev)
        }}
        onKeyDown={(e) => {
            if (disabled) return

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()

            setSearch('')
            setIsOpen((prev) => !prev)
          }
        }}
      >
        <span
          className={`flex-1 truncate text-sm ${
            value ? 'text-foreground' : 'text-foreground_faded'
          }`}
        >
          {value ||
            t(placeholderKey)}
        </span>

        <Icon
          path={mdiChevronDown}
          size="regular"
          className="text-foreground shrink-0"
        />
      </div>

      <DropdownItems
        isOpen={isOpen}
        className="top-full left-0 mt-1 w-full max-h-96 overflow-hidden scrollbar-none bg-container_background"
      >
        <div className="sticky top-0 z-20 bg-container_background px-2 py-2">
          <input
            ref={searchRef}
            value={search}
            type="text"
            placeholder={t(searchPlaceholderKey)}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                closeDropdown()
              }
            }}
            className="w-full h-8 px-3 text-sm rounded-md bg-container_background outline outline-1 outline-offset-[-1px] outline-border_highlight_10"
          />
        </div>

        <div className="max-h-80 overflow-y-auto scrollbar-none">
        {filteredOptions.map((option) => (
         <DropdownListItem
            key={option.value}
            title={option.label}
            isSelected={value === option.value}
            onClick={() => {
              onChange(option.value)
                closeDropdown()
              }}
          />
        ))}

        {filteredOptions.length === 0 && (
          <div className="px-3 py-2 text-sm text-foreground_faded">
            {t(noResultsKey)}
          </div>
     )}
        </div>
    </DropdownItems>
    </div>
  )
}